'use client';

import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { motion } from 'framer-motion';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LeviButton from '@/app/components/LeviButton';
import Scribble from '@/app/components/suppliers/Scribble';
import type { DoorSceneHandle } from '@/app/components/suppliers/DoorScene';

const DoorScene = dynamic(() => import('@/app/components/suppliers/DoorScene'), { ssr: false });

const lineAnim = (i: number) => ({
  initial: { y: '110%' },
  animate: { y: '0%' },
  transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
});

export default function SupplierHero() {
  const root = useRef<HTMLElement>(null);
  const handle = useRef<DoorSceneHandle>({ progress: 0, pointer: { x: 0, y: 0 }, intro: 0 });
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const el = root.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { rootMargin: '10% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // page-load intro for the door (creak ajar)
    gsap.to(handle.current, { intro: 1, duration: 1.6, delay: 0.6, ease: 'power3.out' });

    if (reduced) { handle.current.progress = 0.25; return; }

    const mm = gsap.matchMedia();
    mm.add('(min-width: 768px)', () => {
      // desktop: pin the hero and walk through the door
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: root.current, start: 'top top', end: '+=140%', pin: true, scrub: 0.5, anticipatePin: 1,
          onUpdate: (self) => { handle.current.progress = self.progress; },
        },
      });
      tl.to('.sup-hero-copy', { y: -60, opacity: 0, ease: 'power2.in', duration: 0.5 }, 0.45)
        .to('.sup-hero-hint', { opacity: 0, duration: 0.2 }, 0);
    });
    mm.add('(max-width: 767px)', () => {
      ScrollTrigger.create({
        trigger: root.current, start: 'top top', end: 'bottom 30%', scrub: 0.6,
        onUpdate: (self) => { handle.current.progress = self.progress; },
      });
    });

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      handle.current.pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      handle.current.pointer.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, { scope: root });

  return (
    <section ref={ root } className='sup-hero relative min-h-[100svh] flex items-center overflow-hidden pt-24 md:pt-16'>
      <div className='container relative grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-4 items-center'>
        <div className='sup-hero-copy md:col-span-7 relative z-10 pt-10 md:pt-16'>
          <h1 className='sup-h1'>
            <span className='sup-line'><motion.span { ...lineAnim(0) }>Prodáváte</motion.span></span>
            <span className='sup-line'>
              <motion.span { ...lineAnim(1) }>
                do <span className='relative inline-block'>gastra?
                  <Scribble kind='converge' trigger='mount' delay={ 1.05 } duration={ 0.8 } width={ 7 } style={ { left: '-1%', right: '-1%', bottom: '-0.14em', height: '0.3em' } } />
                </span>
              </motion.span>
            </span>
          </h1>

          <motion.p className='sup-lead mt-8 md:mt-10 max-w-[22ch]'
                    initial={ { opacity: 0, y: 18 } } animate={ { opacity: 1, y: 0 } } transition={ { duration: 0.8, delay: 0.75, ease: 'easeOut' } }>
            Rozumíme gastru.<br />Dostaneme vás do podniků.
          </motion.p>

          <LeviButton href='#kontakt' color='primary' className='mt-8 md:mt-10 text-lg md:text-xl'
                      initial={ { opacity: 0, scale: 0.6 } } animate={ { opacity: 1, scale: 1 } }
                      transition={ { duration: 0.8, delay: 1.0, type: 'spring', damping: 12, stiffness: 180 } }>
            Chci návrh zdarma
          </LeviButton>

          <motion.p className='sup-body mt-10 md:mt-14 max-w-[42ch] text-[0.95rem]! md:text-[1.05rem]! opacity-80'
                    initial={ { opacity: 0 } } animate={ { opacity: 0.8 } } transition={ { duration: 1, delay: 1.4 } }>
            Pro výrobce a dodavatele, kteří prodávají restauracím, kavárnám, hotelům a barům. Vybavení, suroviny, technologie, služby.
          </motion.p>
        </div>

        <motion.div className='md:col-span-5 relative h-[100vw] md:h-[84vh] md:min-h-[560px] -mx-6 md:mx-0 md:-mr-[4vw]'
                    initial={ { opacity: 0 } } animate={ { opacity: 1 } } transition={ { duration: 1.2, delay: 0.5 } }>
          <DoorScene handle={ handle } active={ visible } className='absolute inset-0' />
          <span className='sr-only'>Kuchyňské lítačky, které se otevírají při scrollování.</span>
        </motion.div>
      </div>

      <motion.div className='sup-hero-hint absolute bottom-3 left-1/2 -translate-x-1/2 hidden md:[@media(min-height:820px)]:flex flex-col items-center gap-1 text-[0.8rem] font-bold opacity-60'
                  initial={ { opacity: 0 } } animate={ { opacity: 0.6 } } transition={ { delay: 2.2, duration: 1 } }>
        <span>Vstupte</span>
        <span className='block w-[2px] h-8 bg-black animate-pulse' />
      </motion.div>
    </section>
  );
}
